import {
  ITabletSubmission,
  ITabletSubmissionStatus,
} from '../Interfaces/ITabletSubmission';

/**
 * Zuordnungs- und Auswahl-Logik der Ident-Service-Erfassungsliste.
 *
 * Bewusst als eigenes Modul ohne React-Abhängigkeit: das ist reine Logik,
 * die sich ohne Rendering testen lässt (siehe tabletSubmissionGrouping.test.ts).
 */

/**
 * Fremdschlüssel-Zugriff auf eine Submission.
 *
 * Das Backend liefert Fremdschlüssel NICHT unter ihrem Spaltennamen: steht ein
 * FK in den `Relations` der Modul-Config, ersetzt der SQL-Generator ihn durch
 * den Primärschlüsselnamen der verknüpften Tabelle
 * (`legalclient.idLegalClient as idLegalClient`). Für TabletSubmission gilt das
 * für alle drei FKs, und zwar in Listen- UND Detail-Endpoint. `customFetcher`
 * gibt `response.json()` unverändert weiter, es gibt also keine Validierung,
 * die das auffangen würde.
 *
 * Beide Schreibweisen lesen — dasselbe Muster wie in LegalTransactionCopy und
 * in LegalPhase/LegalDependency/utils.
 */
export function submissionClientId(
  submission: ITabletSubmission | undefined | null
): number | null {
  if (!submission) return null;
  return (
    submission.idLegalClient ?? submission.LegalClient_idLegalClient ?? null
  );
}

export function submissionTransactionId(
  submission: ITabletSubmission | undefined | null
): number | null {
  if (!submission) return null;
  return (
    submission.idLegalTransaction ??
    submission.LegalTransaction_idLegalTransaction ??
    null
  );
}

/**
 * Rangfolge für die Zeile, die pro Beteiligtem zuerst angezeigt wird.
 *
 * `submitted` steht oben, weil das der einzige Status ist, der eine Handlung
 * des Notariats verlangt: die Daten liegen da und wollen geprüft werden.
 * `pending_input` wartet dagegen auf den Mandanten. Vorher war es umgekehrt —
 * dann verdeckte ein später verschickter Tablet-Auftrag eine schon
 * eingegangene Portal-Einreichung.
 */
export const STATUS_RANK: Record<ITabletSubmissionStatus, number> = {
  submitted: 5,
  pending_input: 4,
  accepted: 3,
  rejected: 2,
  cancelled: 1,
};

function createdAtOf(submission: ITabletSubmission): string {
  return submission.Created_at ?? '';
}

/** Neueste zuerst — Reihenfolge der aufklappbaren Historie. */
export function sortByNewest(arr: ITabletSubmission[]): ITabletSubmission[] {
  return [...arr].sort((a, b) => createdAtOf(b).localeCompare(createdAtOf(a)));
}

/** Die Submission, die in der Beteiligten-Zeile angezeigt wird. */
export function pickMostRelevant(
  arr: ITabletSubmission[]
): ITabletSubmission | undefined {
  if (!arr.length) return undefined;
  return [...arr].sort((a, b) => {
    const byRank = STATUS_RANK[b.Status] - STATUS_RANK[a.Status];
    if (byRank !== 0) return byRank;
    return createdAtOf(b).localeCompare(createdAtOf(a));
  })[0];
}

export interface GroupedSubmissions {
  /** Alle Submissions je Beteiligtem, neueste zuerst. */
  byParticipant: Map<number, ITabletSubmission[]>;
  /** Eingänge ohne Beteiligten-Zuordnung (freier Modus / Selbst-Auswahl). */
  unbound: ITabletSubmission[];
}

/**
 * Gruppiert die Submissions eines Vorgangs nach Beteiligtem.
 *
 * Submissions eines anderen Vorgangs werden verworfen. Der Listen-Endpoint
 * filtert das serverseitig, aber die Gruppierung soll nicht davon abhängen:
 * genau diese fehlende Prüfung hat einen stillen Filter-Ausfall im Backend bis
 * in die UI durchschlagen lassen.
 */
export function groupSubmissions(
  submissions: ITabletSubmission[],
  idLegalTransaction: number
): GroupedSubmissions {
  const byParticipant = new Map<number, ITabletSubmission[]>();
  const unbound: ITabletSubmission[] = [];

  for (const submission of submissions) {
    const idLt = submissionTransactionId(submission);
    if (idLt != null && idLt !== idLegalTransaction) continue;

    const idClient = submissionClientId(submission);
    if (idClient) {
      const arr = byParticipant.get(idClient) ?? [];
      arr.push(submission);
      byParticipant.set(idClient, arr);
    } else {
      unbound.push(submission);
    }
  }

  // forEach statt for...of: das TS-Target des Projekts erlaubt kein Iterieren
  // ueber eine Map ohne downlevelIteration.
  byParticipant.forEach((arr, idClient) => {
    byParticipant.set(idClient, sortByNewest(arr));
  });
  return { byParticipant, unbound: sortByNewest(unbound) };
}
